import React from 'react';
import { View, Svg, Rect, Text, Circle, Line } from '@react-pdf/renderer';
import type { Goal } from '../types';

interface GoalAnalyticsPDFProps {
  goals: Goal[];
  width?: number;
  height?: number;
}

const GoalAnalyticsPDF: React.FC<GoalAnalyticsPDFProps> = ({ 
  goals,
  width = 200,
  height = 150
}) => {
  // Calculate zones (4x4 grid)
  const getZone = (x: number, y: number): number => {
    const col = Math.floor(x / 25);
    const row = Math.floor(y / 25);
    return row * 4 + col;
  };

  // Count goals in each zone
  const zoneCount = new Array(16).fill(0);
  goals.forEach(goal => {
    const zone = getZone(goal.xPosition, goal.yPosition);
    if (zone >= 0 && zone < 16) {
      zoneCount[zone]++;
    }
  });

  // Calculate percentages
  const totalGoals = goals.length;
  const zonePercentages = zoneCount.map(count => 
    totalGoals > 0 ? (count / totalGoals) * 100 : 0
  );

  return (
    <View style={{ width, height }}>
      <Svg viewBox="0 0 100 100" width={width} height={height}>
        {/* Background */}
        <Rect x={0} y={0} width={100} height={100} fill="#f3f4f6" />
        
        {/* Goal frame */}
        <Line x1={0} y1={0} x2={100} y2={0} stroke="#ef4444" strokeWidth={2} />
        <Line x1={0} y1={0} x2={0} y2={100} stroke="#ef4444" strokeWidth={2} />
        <Line x1={100} y1={0} x2={100} y2={100} stroke="#ef4444" strokeWidth={2} />

        {/* Zone grid */}
        {[25, 50, 75].map((pos) => (
          <React.Fragment key={pos}>
            <Line x1={pos} y1={0} x2={pos} y2={100} stroke="#d1d5db" strokeWidth={0.5} />
            <Line x1={0} y1={pos} x2={100} y2={pos} stroke="#d1d5db" strokeWidth={0.5} />
          </React.Fragment>
        ))}

        {/* Zone percentages */}
        {zonePercentages.map((percentage, index) => {
          const col = index % 4;
          const row = Math.floor(index / 4);
          const x = col * 25;
          const y = row * 25;

          if (percentage > 0) {
            return (
              <React.Fragment key={index}>
                {/* Zone background */}
                <Rect
                  x={x}
                  y={y}
                  width={25}
                  height={25}
                  fill={`rgba(239, 68, 68, ${percentage / 100})`}
                  fillOpacity={0.3}
                />
                {/* Percentage circle */}
                <Circle
                  cx={x + 12.5}
                  cy={y + 12.5}
                  r={8}
                  fill="#ef4444"
                />
                {/* Percentage text */}
                <Text
                  x={x + 12.5}
                  y={y + 12.5}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{
                    fontSize: 6,
                    fontWeight: 'bold',
                    fill: '#ffffff'
                  }}
                >
                  {`${percentage.toFixed(0)}%`}
                </Text>
              </React.Fragment>
            );
          }
          return null;
        })}
      </Svg>
    </View>
  );
};

export default GoalAnalyticsPDF;